import { BathProductPage, BathProductConfig } from "@/components/bath/BathProductPage";
import { VelaWellnessFeatures } from "@/components/bath/VelaWellnessFeatures";

const config: BathProductConfig = {
  featureShowcase: <VelaWellnessFeatures />,
  slug: "vela-freestanding",
  category: "Freestanding Acrylic Bathtub",
  productName: "VELA Slim-Rim Freestanding Acrylic Bathtub SP1836",
  shortName: "VELA Freestanding",
  sku: "VELA-SP1836-1870",
  schemaCategory: "Bathtubs > Freestanding > Acrylic",
  productMaterial: "Sanitary Grade PMMA Acrylic with Fiberglass & Resin Reinforcement",
  metaTitle: "VELA SP1836 Slim-Rim Acrylic Freestanding Bathtub 1870mm",
  metaDescription:
    "Slim-rim PMMA acrylic freestanding tub with bubble massage, surf jets, smart panel control and chromotherapy. Wellness-suite ready.",
  heroEyebrow: "Project Solutions · Freestanding Acrylic Bathtub",
  heroHeadline: (
    <>
      Slim-Rim Freestanding{" "}
      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
        Acrylic Bathtub
      </span>{" "}
      — Architectural Silhouette
    </>
  ),
  heroIntro:
    "A 1870mm freestanding acrylic bathtub engineered around a precision slim-rim profile. Glossy sanitary-grade PMMA over a fiberglass and resin-reinforced shell, mounted on a concealed stainless steel frame for hospitality-grade rigidity.",
  premiumSummary:
    "Pure PMMA acrylic, alkali-free fiberglass and resin reinforcement, integrated wood backing, drainer and overflow included, and a stainless steel support with height-adjustable feet — supplied as a single ready-to-install freestanding unit for residential and hospitality projects across Europe.",
  galleryImages: [
    {
      src: "/images/bath/sp1836/hero.jpg",
      alt: "VELA SP1836 slim-rim freestanding acrylic bathtub hero shot",
      title: "VELA Freestanding — Hero",
      caption: "Hero · Slim-rim silhouette",
      description: "Front-facing hero of the VELA SP1836 1870mm freestanding acrylic bathtub.",
    },
    {
      src: "/images/bath/sp1836/angle.jpg",
      alt: "VELA SP1836 acrylic bathtub three-quarter angle view",
      title: "VELA Freestanding — Angle",
      caption: "Studio · Three-quarter view",
      description: "Three-quarter studio view emphasising the gloss PMMA surface and slim rim.",
    },
    {
      src: "/images/bath/sp1836/detail.jpg",
      alt: "VELA acrylic bathtub slim rim and drainer detail",
      title: "VELA Freestanding — Rim Detail",
      caption: "Detail · Slim-rim & drainer",
      description: "Macro detail of the slim-rim profile, polished chrome drainer and overflow.",
    },
    {
      src: "/images/bath/sp1836/side.jpg",
      alt: "VELA SP1836 acrylic bathtub side profile",
      title: "VELA Freestanding — Side Profile",
      caption: "Profile · Side elevation",
      description: "Side profile showing the freestanding floor-mount footprint and concealed stainless support.",
    },
    {
      src: "/images/bath/sp1836/top.jpg",
      alt: "VELA SP1836 acrylic bathtub top-down studio view",
      title: "VELA Freestanding — Top View",
      caption: "Studio · Top-down",
      description: "Top-down studio view of the VELA bathtub interior with centre drain.",
    },
    {
      src: "/images/bath/sp1836/lifestyle.jpg",
      alt: "VELA freestanding acrylic bathtub installed in luxury bathroom",
      title: "VELA Freestanding — Lifestyle",
      caption: "Lifestyle · Suite installation",
      description: "Lifestyle scene: VELA freestanding bathtub specified in a contemporary luxury bathroom.",
    },
  ],
  specs: [
    { label: "Dimensions", value: "1870 × 780 × 930 mm (L × W × H)" },
    { label: "Material", value: "100% Pure Sanitary-Grade PMMA Acrylic" },
    { label: "Reinforcement", value: "Alkali-free Fiberglass + Resin + Wood Backing" },
    { label: "Finish", value: "High-gloss · Scratch / Stain / Fade Resistant" },
    { label: "Installation", value: "Freestanding · Floor-mounted" },
    { label: "Support Frame", value: "Stainless Steel · Height-adjustable Feet" },
    { label: "Drainage", value: "Drainer & Overflow Included (Chrome)" },
    { label: "Faucet", value: "Not Included (Standard EU rough-in)" },
    { label: "Bubble Massage", value: "Air-blower bubble system · perimeter floor jets" },
    { label: "Surf Massage", value: "Hydro surf jets · adjustable directional nozzles" },
    { label: "Smart Panel", value: "Digital touch control · jet / blower / light / timer" },
    { label: "Magical Lights", value: "Underwater LED chromotherapy · multi-colour cycle" },
    { label: "Certification", value: "CE Marked · 3C Certified" },
    { label: "Warranty", value: "5 years structural · 2 years hardware" },
    { label: "MOQ (Container)", value: "Bulk pricing from 20+ units" },
  ],
  longDescription: [
    "VELA SP1836 is a 1870mm slim-rim freestanding acrylic bathtub built for European projects that need the visual gravity of a designer tub without the weight, cost and lead-time of stone or cast iron. The shell is formed from 100% pure sanitary-grade PMMA acrylic, then reinforced with alkali-free fiberglass, resin and a wood backing layer for a rigid, warm, quiet structure.",
    "Beyond the silhouette, VELA is specified as a full wellness bathtub. A whisper-quiet air blower drives a perimeter bubble massage system for soft, effervescent immersion, while directional hydro surf jets deliver targeted muscle relief along the back and legs. Both systems are independently controlled — guests can run bubbles, surf massage, or both layered together.",
    "The integrated smart panel is a digital touch console mounted on the rim. It controls jet intensity, blower speed, chromotherapy lighting, and a safety timer with a single illuminated interface — no physical knobs, no exposed wiring. Underwater magical lights cycle through a calibrated chromotherapy palette (warm amber, deep blue, emerald, violet, soft white) for spa-grade ambience.",
    "Specification is deliberately straightforward. The bathtub ships with its drainer and overflow assembly pre-fitted, and sits on a concealed stainless steel support frame with pre-leveled, height-adjustable feet — no on-site fabrication required. Faucet is supplied separately so designers can match brassware to project palette (chrome, brushed nickel, brushed gold or matte black).",
    "Engineered for residential developments, boutique hotels, branded residences and high-end rental projects. CE marked and 3C certified, with MOQ-ready supply, predictable container lead times and OEM finishing options for project-scale orders across Europe.",
  ],
  quickFeatures: [
    "100% pure sanitary-grade PMMA acrylic shell",
    "Alkali-free fiberglass + resin + wood reinforcement",
    "Slim-rim architectural profile",
    "Bubble massage · perimeter air-blower system",
    "Surf massage · directional hydro jets",
    "Smart panel · digital touch control",
    "Magical lights · underwater LED chromotherapy",
    "Drainer & overflow included",
    "Stainless steel support · height-adjustable feet",
    "CE marked · 3C certified",
  ],
  additionalProperties: [
    { name: "Length", value: "1870", unitText: "mm" },
    { name: "Width", value: "780", unitText: "mm" },
    { name: "Height", value: "930", unitText: "mm" },
    { name: "Material", value: "PMMA Acrylic" },
    { name: "Installation Type", value: "Freestanding" },
  ],
  faqs: [
    {
      question: "What is the VELA SP1836 bathtub made of?",
      answer:
        "100% pure sanitary-grade PMMA acrylic, reinforced with alkali-free fiberglass, resin and a wood backing layer for rigidity, warmth and acoustic damping.",
    },
    {
      question: "Is the drainer and faucet included?",
      answer:
        "The drainer and overflow assembly are included and pre-fitted. The faucet is supplied separately so designers can match the brassware finish to the project palette.",
    },
    {
      question: "What support frame does it use?",
      answer:
        "A concealed stainless steel support frame with pre-leveled, height-adjustable feet — engineered for hospitality-grade rigidity and easy on-site leveling.",
    },
    {
      question: "What is the minimum order quantity?",
      answer:
        "Bulk project pricing is structured from 20+ units. Smaller orders are available on request — contact our project team for current lead times.",
    },
  ],
};

const VelaFreestanding = () => <BathProductPage config={config} />;
export default VelaFreestanding;
